import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, ScatterChart, Scatter, Area, AreaChart, ComposedChart, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from 'recharts';

const COLORS = {
  red: '#ff6b6b',
  teal: '#4ecdc4',
  yellow: '#ffeaa7',
  green: '#96ceb4',
  blue: '#45b7d1',
  orange: '#ffb366',
  purple: '#c39bd3',
  grey: '#dfe6e9',
  darkGrey: '#636e72'
};

const EXECUTIVE_SUMMARY_IMAGES = {
  title: '/predictions_assets/2029/images/title.jpg',
  cta: '/predictions_assets/2029/images/title.jpg'
};

const publicBaseUrl = process.env.PUBLIC_URL ? process.env.PUBLIC_URL.replace(/\/$/, '') : '';
const resolveReportAssetUrl = (src) => {
  if (!src || typeof src !== 'string') return src;
  if (!publicBaseUrl) return src;
  if (/^https?:\/\//i.test(src)) return src;
  if (src.startsWith(publicBaseUrl + '/')) return src;
  if (src.startsWith('/')) return `${publicBaseUrl}${src}`;
  return src;
};

// Intervention Urgency Distribution
const interventionUrgencyData = [
  { urgency: 'URGENT\n(9-12mo)', count: 5, color: COLORS.red },
  { urgency: 'CRITICAL\n(12-18mo)', count: 8, color: COLORS.orange },
  { urgency: 'PREVENTIVE\n(18-24mo)', count: 15, color: COLORS.yellow },
  { urgency: 'ACTIONABLE\n(24-36mo)', count: 26, color: COLORS.blue },
  { urgency: 'POSITIVE\n(Reinforce)', count: 6, color: COLORS.green }
];

// Success Probability by Timing
const successByTimingData = [
  { timing: 'Within Window', avgSuccess: 64.7, range: '48-78%' },
  { timing: 'At Window Edge', avgSuccess: 45, range: '30-60%' },
  { timing: 'Past Window', avgSuccess: 22, range: '10-35%' }
];

// Signal Strength Distribution
const signalStrengthData = [
  { strength: 'HIGH', count: 8, description: 'Documented, multiple sources' },
  { strength: 'MEDIUM-HIGH', count: 22, description: 'Strong leading indicators' },
  { strength: 'MEDIUM', count: 21, description: 'Directional signals' },
  { strength: 'LOW-MEDIUM', count: 9, description: 'Weak signals/exploratory' }
];

// Sector Model Maturity
const sectorMaturityData = [
  { sector: 'Screen', maturity: 95, predictions: 12, confidence: 'High' },
  { sector: 'Music', maturity: 88, predictions: 10, confidence: 'Med-High' },
  { sector: 'Infrastructure', maturity: 85, predictions: 8, confidence: 'High' },
  { sector: 'Education', maturity: 75, predictions: 5, confidence: 'Medium' },
  { sector: 'Advertising', maturity: 72, predictions: 5, confidence: 'Medium' },
  { sector: 'Creators', maturity: 70, predictions: 5, confidence: 'Medium' },
  { sector: 'Games', maturity: 68, predictions: 4, confidence: 'Medium' },
  { sector: 'Social', maturity: 65, predictions: 4, confidence: 'Med-Low' },
  { sector: 'Performing', maturity: 60, predictions: 3, confidence: 'Medium' },
  { sector: 'Fashion', maturity: 55, predictions: 2, confidence: 'Med-Low' },
  { sector: 'Craft', maturity: 50, predictions: 2, confidence: 'Med-Low' },
  { sector: 'Publishing', maturity: 48, predictions: 0, confidence: 'Low' }
];

// Cross-Cutting Forces Impact
const crossCuttingForcesData = [
  { force: 'AI Displacement', impact: 85, sectors: 11 },
  { force: 'Housing Constraints', impact: 72, sectors: 9 },
  { force: 'Burnout/Precarity', impact: 68, sectors: 12 },
  { force: 'Shadow Economy', impact: 55, sectors: 8 },
  { force: 'Regional Concentration', impact: 62, sectors: 10 }
];

// Model Architecture Overview
const modelArchitectureData = [
  { layer: 'Data Sources', nodes: 73, description: 'Real-time feeds' },
  { layer: 'Preprocessing', nodes: 4, description: 'Data cleaning' },
  { layer: 'Demand Analysis', nodes: 33, description: 'Market signals' },
  { layer: 'Hiring Forecast', nodes: 14, description: 'Employment prediction' },
  { layer: 'Training Analysis', nodes: 12, description: 'Skills pipeline' },
  { layer: 'Cross-Cutting', nodes: 136, description: 'AI, housing, burnout' },
  { layer: 'Strategic', nodes: 18, description: 'Policy interventions' },
  { layer: 'Outputs', nodes: 4, description: 'Reports & APIs' },
  { layer: 'Governance', nodes: 25, description: 'Validation & quality' }
];

// Countermeasure Types Distribution
const countermeasureTypesData = [
  { type: 'Regulatory', count: 18, avgSuccess: 62 },
  { type: 'Market-Based', count: 15, avgSuccess: 78 },
  { type: 'Educational', count: 12, avgSuccess: 58 },
  { type: 'Infrastructure', count: 10, avgSuccess: 55 },
  { type: 'Collective Action', count: 8, avgSuccess: 68 },
  { type: 'Funding/Subsidy', count: 7, avgSuccess: 65 }
];

// Intervention Window Timeline
const interventionTimelineData = [
  { month: 0, optimal: 78, acceptable: 78, postWindow: 78 },
  { month: 6, optimal: 75, acceptable: 75, postWindow: 75 },
  { month: 12, optimal: 68, acceptable: 68, postWindow: 68 },
  { month: 18, optimal: 55, acceptable: 55, postWindow: 55 },
  { month: 24, optimal: 38, acceptable: 38, postWindow: 38 },
  { month: 30, optimal: 22, acceptable: 22, postWindow: 22 },
  { month: 36, optimal: 15, acceptable: 15, postWindow: 15 }
];

// Geographic Concentration Risk
const geographicRiskData = [
  { region: 'London/SE', concentration: 42, risk: 'High', sectors: 'All' },
  { region: 'Manchester', concentration: 18, risk: 'Medium', sectors: 'Screen, Music' },
  { region: 'Scotland', concentration: 12, risk: 'Medium', sectors: 'Screen, Games' },
  { region: 'Bristol/Bath', concentration: 8, risk: 'Low-Med', sectors: 'Screen, Creative' },
  { region: 'Birmingham', concentration: 7, risk: 'Low', sectors: 'Diverse' },
  { region: 'Other', concentration: 13, risk: 'Low', sectors: 'Distributed' }
];

// Validated Case Studies Success
const caseStudyData = [
  { study: 'Manchester\nAnimation', forecast: 'Q4 2022', outcome: 'Q2 2024', accuracy: 92 },
  { study: 'Quebec\nVFX', forecast: 'Q1 2024', outcome: 'Q3 2024', accuracy: 88 },
  { study: 'Streaming\nDecline', forecast: 'Q2 2023', outcome: 'Q4 2024', accuracy: 85 }
];

const ReportImage = ({ src, alt, className, style }) => {
  const [didError, setDidError] = useState(false);
  const resolvedSrc = resolveReportAssetUrl(src);
  if (!resolvedSrc || didError) {
    return (
      <div className={className ? `${className} report-image-placeholder` : 'report-image-placeholder'} style={style}>
        {alt || 'Image unavailable'}
      </div>
    );
  }
  return <img src={resolvedSrc} alt={alt || ''} className={className} style={style} onError={() => setDidError(true)} />;
};

const CalloutBox = ({ color, title, children, style }) => (
  <div style={{
    border: `2px solid ${color}`,
    borderRadius: '4px',
    padding: '10pt',
    margin: '8px 0',
    background: color === COLORS.red ? '#fff5f5' : color === COLORS.teal ? '#f0f9ff' : color === COLORS.orange ? '#fff8f0' : '#fef9f0',
    ...style
  }}>
    <div style={{ fontSize: '9pt', fontWeight: 700, color: color, marginBottom: '4px' }}>{title}</div>
    <div style={{ fontSize: '8.5pt', lineHeight: '1.4', color: '#2c3e50' }}>{children}</div>
  </div>
);

const waitForImagesToLoad = async () => {
  const imgs = Array.from(document.images || []);
  await Promise.all(imgs.map(async (img) => {
    if (!img || img.complete) {
      if (typeof img?.decode === 'function') {
        try { await img.decode(); } catch {}
      }
      return;
    }
    await new Promise((resolve) => {
      const done = () => resolve();
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    });
    if (typeof img?.decode === 'function') {
      try { await img.decode(); } catch {}
    }
  }));
};

const ExecutiveSummaryReport = () => {
  const [pagedReady, setPagedReady] = useState(false);
  const didInitPagedRef = useRef(false);

  const isPagedMode = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    return params.get('paged') === '1';
  }, []);

  useEffect(() => {
    if (!isPagedMode || didInitPagedRef.current) return;
    didInitPagedRef.current = true;
    const runPaged = async () => {
      try {
        await waitForImagesToLoad();
        if (typeof document !== 'undefined' && document.fonts?.ready) {
          try { await document.fonts.ready; } catch {}
        }
        setPagedReady(true);
      } catch (err) {
        console.error('[ExecutiveSummaryReport] Print-friendly mode init failed', err);
        setPagedReady(true);
      }
    };
    runPaged();
  }, [isPagedMode]);

  const openPagedView = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('paged', '1');
    window.open(url.toString(), '_blank', 'noopener,noreferrer');
  };

  const exitPagedView = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('paged');
    window.location.assign(url.toString());
  };

  const handlePrint = () => window.print();

  return (
    <div className={isPagedMode ? 'report-container paged-mode' : 'report-container'}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Libre+Franklin:wght@400;600;700;900&display=swap');
        * { box-sizing: border-box; }
        body { margin: 0; padding: 0; font-family: 'Libre Franklin', sans-serif; background: white; color: #2d3436; line-height: 1.6; }
        .report-container { width: 100%; max-width: min(100%, 1024px); margin: 0 auto; background: white; }
        .report-container.paged-mode { max-width: none; margin: 0; background: transparent; }
        .content-page { padding: 15mm 20mm; min-height: 100vh; background: white; }
        .paged-mode .content-page { min-height: auto; page-break-after: always; }
        @page { size: A4 landscape; margin: 0; }
        @media print {
          html, body { background: #ffffff !important; color: #111111 !important; }
          .report-container { background: #ffffff !important; color: #111111 !important; }
          * { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
        }
        .title-page { display: block; height: 210mm; position: relative; background: #ffffff; padding: 0; overflow: hidden; page-break-inside: avoid; break-inside: avoid; page-break-after: always; break-after: page; }
        .cover-hero { position: absolute; inset: 0; }
        .cover-image { width: 100%; height: 100%; object-fit: cover; display: block; margin: 0; border: none; }
        .cover-shade { position: absolute; inset: 0; background: linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.5) 45%, rgba(0,0,0,0.8) 70%, rgba(0,0,0,1) 100%); }
        .cover-lockup { position: absolute; left: 50%; top: 58%; transform: translate(-50%, -50%); width: 88%; text-align: center; color: white; }
        .cover-kicker { font-size: 14pt; letter-spacing: 0.22em; text-transform: uppercase; font-weight: 700; color: #FFFF66; margin-bottom: 8mm; }
        .cover-title { font-size: 52pt; font-weight: 900; letter-spacing: -1.5px; line-height: 1.0; margin: 0; text-transform: uppercase; }
        .cover-subtitle { margin-top: 8mm; font-size: 16pt; font-weight: 600; max-width: 240mm; margin-left: auto; margin-right: auto; line-height: 1.4; }
        .cover-meta { margin-top: 12mm; font-size: 11pt; font-weight: 600; opacity: 0.9; }
        .toc-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10mm; margin-top: 10mm; }
        .toc-list { list-style: none; padding: 0; margin: 0; }
        .toc-item { display: flex; justify-content: space-between; gap: 8mm; border-bottom: 1px solid #dfe6e9; padding: 4mm 0; font-size: 11pt; }
        .toc-item a { text-decoration: none; color: #2d3436; font-weight: 600; }
        .toc-item a:hover { color: #0984e3; }
        .break-after { page-break-after: always; break-after: page; }
        .kicker { margin: 10mm 0 4mm; font-size: 10pt; font-weight: 700; letter-spacing: 0.18em; text-transform: uppercase; color: #111111; }
        h1 { font-size: 32pt; font-weight: 900; color: #2d3436; margin: 0 0 8mm 0; line-height: 1.2; letter-spacing: -0.5px; }
        h2 { font-size: 18pt; font-weight: 700; color: #2d3436; margin: 8mm 0 4mm; border-bottom: 2px solid #2d3436; padding-bottom: 2mm; }
        h3 { font-size: 14pt; font-weight: 700; color: #2d3436; margin: 6mm 0 3mm; }
        p { font-size: 10pt; line-height: 1.6; margin: 0 0 8pt; text-align: justify; }
        .stat-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin: 15px 0; }
        .stat-box { background: #f8f9fa; border: 2px solid #2d3436; padding: 12px; text-align: center; }
        .stat-value { font-size: 24pt; font-weight: 900; color: #2d3436; line-height: 1; }
        .stat-label { font-size: 8pt; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; margin-top: 4px; color: #636e72; }
      `}</style>

      {!isPagedMode && (
        <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 1000, display: 'flex', gap: '10px' }}>
          <button onClick={handlePrint} style={{ padding: '12px 24px', background: '#667eea', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}>Print Report</button>
          <button onClick={openPagedView} style={{ padding: '12px 24px', background: '#667eea', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}>PDF View</button>
        </div>
      )}

      {isPagedMode && (
        <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 1000 }}>
          <button onClick={exitPagedView} style={{ padding: '12px 24px', background: '#667eea', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}>Exit PDF View</button>
        </div>
      )}

      <div className="title-page break-after">
        <div className="cover-hero">
          <ReportImage src={EXECUTIVE_SUMMARY_IMAGES.title} alt="Creative Industries Predictions 2029" className="cover-image" />
          <div className="cover-shade" />
        </div>
        <div className="cover-lockup">
          <div className="cover-kicker">OBSOLETE.COM PRESENTS</div>
          <div className="cover-title">60 DYSTOPIAN FUTURES<br/>60 WAYS TO PREVENT THEM</div>
          <div className="cover-subtitle">A Predictive Model Analysis of UK Creative Industries Workforce Outcomes 2025-2029</div>
          <div className="cover-meta">Based on 433 Nodes • 1,508 Causal Relationships • 73 Real-Time Data Sources</div>
        </div>
      </div>

      <div className="content-page toc-page break-after">
        <h1>Sector Reports</h1>
        <p style={{ fontSize: '11pt', marginBottom: '15px', fontWeight: 600 }}>Navigate to detailed predictions and countermeasures by sector. Each report includes 5 predictions with dystopian scenarios, mitigated outcomes, signal validation, and intervention windows.</p>
        
        <div className="toc-grid">
          <ul className="toc-list">
            <li className="toc-item"><a href="/predictions/reports/screen">Screen Industries</a><span>5 predictions</span></li>
            <li className="toc-item"><a href="/predictions/reports/music">Music</a><span>5 predictions</span></li>
            <li className="toc-item"><a href="/predictions/reports/games">Games</a><span>5 predictions</span></li>
            <li className="toc-item"><a href="/predictions/reports/fashion">Fashion</a><span>5 predictions</span></li>
            <li className="toc-item"><a href="/predictions/reports/advertising">Advertising</a><span>5 predictions</span></li>
            <li className="toc-item"><a href="/predictions/reports/creators">Creator Economy</a><span>5 predictions</span></li>
          </ul>
          <ul className="toc-list">
            <li className="toc-item"><a href="/predictions/reports/craft">Craft</a><span>5 predictions</span></li>
            <li className="toc-item"><a href="/predictions/reports/infrastructure">Infrastructure</a><span>5 predictions</span></li>
            <li className="toc-item"><a href="/predictions/reports/education">Education</a><span>5 predictions</span></li>
            <li className="toc-item"><a href="/predictions/reports/performing">Performing Arts</a><span>5 predictions</span></li>
            <li className="toc-item"><a href="/predictions/reports/ai">Artificial Intelligence</a><span>5 predictions</span></li>
            <li className="toc-item"><a href="/predictions/reports/social">Social Platforms</a><span>5 predictions</span></li>
          </ul>
        </div>

        <CalloutBox color={COLORS.red} title="CRITICAL DISCLAIMER" style={{ marginTop: '20px' }}>
          These are not predictions of what WILL happen. These are forecasts of what BECOMES LIKELY if current trajectories continue. The difference matters: predictions are deterministic; forecasts are conditional. Every scenario in this document is avoidable. That is the entire point.
        </CalloutBox>
      </div>

      <div className="content-page">
        <h1>Executive Summary</h1>
        
        <h2>The 36-Month Window</h2>
        <p>These predictions aren't speculative. They're documented. The patterns are visible in 2025 data. The futures described here become probable around Q2 2026 if no action is taken. But that's the point: they're not predetermined yet.</p>
        
        <p>This document presents 60 predictions derived from a 433-node predictive model that forecasts creative sector workforce outcomes 36 months in advance. Each prediction shows both outcomes: the dystopian future if we do nothing, and the mitigated future if we act within the intervention window. The average countermeasure success rate is 64.7% if action is taken within the prescribed timeframes. After that window closes, prevention becomes damage control.</p>

        <div className="stat-grid">
          <div className="stat-box">
            <div className="stat-value">60</div>
            <div className="stat-label">Predictions</div>
          </div>
          <div className="stat-box">
            <div className="stat-value">433</div>
            <div className="stat-label">Model Nodes</div>
          </div>
          <div className="stat-box">
            <div className="stat-value">1,508</div>
            <div className="stat-label">Causal Links</div>
          </div>
          <div className="stat-box">
            <div className="stat-value">64.7%</div>
            <div className="stat-label">Avg Success Rate</div>
          </div>
        </div>

        <div style={{ marginTop: '15px' }}>
          <p style={{ fontSize: '9pt', fontWeight: 700, marginBottom: '8px' }}>Intervention Urgency Distribution (60 Predictions)</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={interventionUrgencyData}>
              <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grey} />
              <XAxis dataKey="urgency" tick={{ fontSize: 7 }} />
              <YAxis tick={{ fontSize: 7 }} />
              <Tooltip />
              <Bar dataKey="count" name="Number of Predictions">
                {interventionUrgencyData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <h2>What This Document Is</h2>
        <p>The UK Creative Industries Predictive Model tracks 433 nodes and 1,508 causal relationships across creative industries, drawing from 73 real-time data sources including Lightcast job postings, ONS employment statistics, Parrot Analytics content demand data, and platform financial reports. The model's core insight is deceptively simple: market demand drives investment decisions, which drive hiring, which drives training needs. When streaming demand declines in 2024, platform budgets get cut in 2025, studios reduce hiring in 2026, and graduates can't find jobs in 2027. The model detects this in 2024, providing a 36-month intervention window.</p>

        <p>This matters because creative sector decisions operate on long lead times. University intake decisions made in 2025 determine graduate outcomes in 2028. Training programme investments made today affect workforce readiness three years out. By the time problems become obvious, they've already been locked in by decisions made years earlier. The model exists to see around that corner.</p>

        <div style={{ marginTop: '15px' }}>
          <p style={{ fontSize: '9pt', fontWeight: 700, marginBottom: '8px' }}>Model Architecture (433 Total Nodes)</p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={modelArchitectureData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grey} />
              <XAxis type="number" tick={{ fontSize: 7 }} />
              <YAxis dataKey="layer" type="category" tick={{ fontSize: 7 }} width={90} />
              <Tooltip />
              <Bar dataKey="nodes" fill={COLORS.blue} name="Nodes" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="content-page">
        <h2>The Architecture of Failure</h2>
        <p>The model reveals patterns in how creative sector crises develop. Understanding these patterns is essential to preventing them.</p>

        <p>The housing crisis in UK production clusters follows a predictable trajectory. Buckinghamshire's housing affordability ratio hit 11.1 times annual earnings in 2025, while UK film spend reached £5.6B requiring 20,770 additional crew. Runners physically cannot afford to get to work. Studios face a choice: internalise living costs through on-site housing (creating company towns) or watch production schedules collapse. The intervention window is 18 months. After that, either councils have built below-market crew housing or studios have become landlords. There's no third option.</p>

        <p>The AI quality gap closure drives a different pattern. In 2023, a Manila animator produced work at 60% the quality of a London animator. By 2027, AI tools close that gap to 85%. UK minimum wage is £12.21 per hour; Manila rates are £3.50. Junior roles move offshore not because skills disappeared but because economic logic became inexorable. The intervention is specialisation: train for cultural storytelling and client relationships that can't be offshored, not execution skills that can. The window is 36 months because university curricula take three years to reform. Wait longer and graduates train for jobs that have already moved.</p>

        <CalloutBox color={COLORS.orange} title="SHADOW ECONOMY DYNAMICS">
          Union contracts ban AI usage while client briefs demand it, creating pressure to cheat. Studios build air-gapped render farms running open-source models to do work nobody admits happened. This works until it doesn't. Client audits discover undisclosed AI usage, triggering copyright infringement claims, contract fraud litigation, tax credit investigations, and insurance policy voidance. The £40M settlement bankrupts studios that thought they were winning. The intervention is legitimacy frameworks: negotiate permitted AI usage (60% assist, 40% human minimum) before shadow economies metastasize. The window is 9 months because undisclosed usage is already happening.
        </CalloutBox>

        <div style={{ marginTop: '15px' }}>
          <p style={{ fontSize: '9pt', fontWeight: 700, marginBottom: '8px' }}>Cross-Cutting Forces Impact Across Sectors</p>
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart data={crossCuttingForcesData}>
              <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grey} />
              <XAxis dataKey="force" tick={{ fontSize: 7 }} angle={-15} textAnchor="end" height={80} />
              <YAxis yAxisId="left" tick={{ fontSize: 7 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 7 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: '7pt' }} />
              <Bar yAxisId="left" dataKey="impact" fill={COLORS.red} name="Impact Score" />
              <Line yAxisId="right" type="monotone" dataKey="sectors" stroke={COLORS.blue} strokeWidth={2} name="Sectors Affected" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <h2>Signal Strength and Confidence</h2>
        <p>Not all predictions carry equal certainty. The model distinguishes between what's already happening, what's likely, and what's possible.</p>

        <p>Eight predictions carry HIGH signal strength, meaning they're documented in 2025 data with multiple independent sources. The housing constraint crisis is HIGH because Buckinghamshire affordability ratios, film spend requiring specific crew numbers, and Knight Frank reports on studio-paid accommodation are all measurable today. Channel 4's October 2025 AI presenter going undetected is HIGH because it happened on broadcast television and was documented. These aren't forecasts of the future; they're documentation of the present with trajectory analysis.</p>

        <div style={{ marginTop: '15px' }}>
          <p style={{ fontSize: '9pt', fontWeight: 700, marginBottom: '8px' }}>Signal Strength Distribution (60 Predictions)</p>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={signalStrengthData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry) => `${entry.strength}: ${entry.count}`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="count"
              >
                {signalStrengthData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={[COLORS.red, COLORS.orange, COLORS.yellow, COLORS.grey][index % 4]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="content-page">
        <h2>Intervention Windows and Success Rates</h2>
        <p>Countermeasures work, but timing determines success rates. The model calculates intervention windows based on when trajectories lock in and countermeasure success probabilities based on historical effectiveness of similar interventions.</p>

        <p>Five predictions require URGENT action within 9-12 months. These are scenarios where harmful dynamics are already active and accelerating. Shadow AI usage is URGENT because undisclosed work is happening now and audit risk compounds with each additional project. Success probability is 58% if Bectu negotiates usage frameworks and insurance companies mandate disclosure within nine months. Wait 18 months and the litigation cascade has already started, dropping success to 20-30% (pure damage control).</p>

        <div style={{ marginTop: '15px' }}>
          <p style={{ fontSize: '9pt', fontWeight: 700, marginBottom: '8px' }}>Success Probability Decline Over Time</p>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={interventionTimelineData}>
              <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grey} />
              <XAxis dataKey="month" tick={{ fontSize: 7 }} label={{ value: 'Months from Detection', position: 'insideBottom', offset: -5, fontSize: 8 }} />
              <YAxis tick={{ fontSize: 7 }} label={{ value: 'Success Probability %', angle: -90, position: 'insideLeft', fontSize: 8 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: '7pt' }} />
              <Area type="monotone" dataKey="optimal" stackId="1" stroke={COLORS.green} fill={COLORS.green} fillOpacity={0.6} name="Optimal Window" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <CalloutBox color={COLORS.blue} title="THE MATHEMATICS ARE BRUTAL">
          Countermeasures attempted within their intervention windows succeed at stated probabilities (avg 64.7%). Countermeasures delayed beyond windows see success rates drop 40-60 percentage points as prevention transitions to damage control. Acting early compounds optionality; waiting eliminates it.
        </CalloutBox>

        <div style={{ marginTop: '15px' }}>
          <p style={{ fontSize: '9pt', fontWeight: 700, marginBottom: '8px' }}>Countermeasure Types and Success Rates</p>
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart data={countermeasureTypesData}>
              <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grey} />
              <XAxis dataKey="type" tick={{ fontSize: 7 }} angle={-15} textAnchor="end" height={70} />
              <YAxis yAxisId="left" tick={{ fontSize: 7 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 7 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: '7pt' }} />
              <Bar yAxisId="left" dataKey="count" fill={COLORS.blue} name="Count" />
              <Line yAxisId="right" type="monotone" dataKey="avgSuccess" stroke={COLORS.green} strokeWidth={2} name="Avg Success %" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <h2>Validated Case Studies</h2>
        <p>The model's predictive capability isn't theoretical; it's validated through documented forecasts made before outcomes occurred.</p>

        <p>Manchester's animation sector decline was forecast in Q4 2022 using demand percentile analysis from Parrot Analytics and commissioning data from UK production reports. The model indicated commissioning declining 35-40% over 18 months, with hiring following 12 months later and graduate employment impact 24 months out. By Q2 2024, the forecasted decline had materialized: animation job postings in Greater Manchester down 62% from 2021 peak, graduate employment probability dropping from 0.68 to 0.31. The intervention window was Q4 2022 to Q2 2023 for university intake reduction. No action was taken. The forecasted collapse occurred.</p>

        <div style={{ marginTop: '15px' }}>
          <p style={{ fontSize: '9pt', fontWeight: 700, marginBottom: '8px' }}>Validated Case Study Accuracy</p>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={caseStudyData}>
              <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grey} />
              <XAxis dataKey="study" tick={{ fontSize: 7 }} />
              <YAxis tick={{ fontSize: 7 }} domain={[0, 100]} />
              <Tooltip />
              <Bar dataKey="accuracy" fill={COLORS.green} name="Forecast Accuracy %" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="content-page">
        <h2>Sector Model Maturity</h2>
        <p>Not all sectors have equal model coverage. Screen and Music have the most mature models with validated case studies and highest confidence. Fashion, Craft, and Publishing have emerging coverage with more scenario exploration than prediction.</p>

        <div style={{ marginTop: '15px' }}>
          <p style={{ fontSize: '9pt', fontWeight: 700, marginBottom: '8px' }}>Sector Model Maturity and Prediction Count</p>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={sectorMaturityData}>
              <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grey} />
              <XAxis dataKey="sector" tick={{ fontSize: 7 }} angle={-15} textAnchor="end" height={60} />
              <YAxis yAxisId="left" tick={{ fontSize: 7 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 7 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: '7pt' }} />
              <Bar yAxisId="left" dataKey="maturity" fill={COLORS.blue} name="Maturity Score" />
              <Line yAxisId="right" type="monotone" dataKey="predictions" stroke={COLORS.orange} strokeWidth={2} name="Predictions" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <h2>Geographic Concentration Risk</h2>
        <p>Creative industries cluster geographically, creating amplified regional risk. When a sector declines, regions dependent on that sector face catastrophic employment losses while diversified regions absorb shocks.</p>

        <div style={{ marginTop: '15px' }}>
          <p style={{ fontSize: '9pt', fontWeight: 700, marginBottom: '8px' }}>Regional Creative Industry Concentration</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={geographicRiskData}>
              <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grey} />
              <XAxis dataKey="region" tick={{ fontSize: 7 }} />
              <YAxis tick={{ fontSize: 7 }} />
              <Tooltip />
              <Bar dataKey="concentration" fill={COLORS.purple} name="Employment Concentration %" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <h2>How to Act on This Information</h2>
        <p>The document supports three action modes depending on urgency and scope.</p>

        <p><strong>Executive Scan (15-30 minutes):</strong> Read this Executive Summary, then scan sector introductions for your area, then read any prediction flagged URGENT or CRITICAL that affects your domain. This tells you whether you face immediate crisis requiring emergency mobilization.</p>

        <p><strong>Strategic Planning (2-3 hours):</strong> Complete read of your sector(s) plus Infrastructure chapter, noting all predictions regardless of urgency. This mode supports annual planning cycles, budget allocation decisions, curriculum reviews, policy development, and investment strategies.</p>

        <p><strong>Deep Implementation (Full day):</strong> Complete document read plus signal validation plus countermeasure feasibility analysis. This mode supports major resource commitments where you need to validate model assumptions against local knowledge before proceeding.</p>

        <CalloutBox color={COLORS.yellow} title="CALENDAR DISCIPLINE IS CRITICAL">
          Intervention windows are not suggestions; they're engineering constraints based on how long systems take to respond. Housing construction takes 18 months minimum. Curriculum reform takes 3 years minimum. Tax credit legislation takes 2 years minimum. Put intervention window deadlines in your calendar now, not when crisis becomes obvious.
        </CalloutBox>
      </div>

      <div className="content-page">
        <h2>The Aesthetic Choice Explained</h2>
        <p>This document is visually and rhetorically aggressive in ways academic reports typically aren't. This requires explanation.</p>

        <p>The dystopian scenarios are written to create emotional response. "The biggest landlord in Buckinghamshire isn't a property developer; it's a film studio" is designed to be unsettling. Studios becoming company towns running total institutions should be unsettling. Sanitizing the description would reduce accuracy, not increase objectivity. The scenario is shocking because the outcome would be shocking.</p>

        <p>The mitigated scenarios maintain tension. They aren't triumphalist success stories; they're "it worked, mostly, with tradeoffs." This reflects reality. Intervention success doesn't mean problems disappear; it means they become manageable rather than catastrophic. The mitigated housing scenario still has 18-month waiting lists and binary ins-and-outs, but it avoids company town lock-in. That's success in real-world policymaking.</p>

        <p>The "Soviet agitprop meets cyberpunk warning label" aesthetic serves a function. Creative sector decision-makers receive countless reports, all formatted neutrally, all making reasonable recommendations, all filed and forgotten. Breaking that pattern requires breaking the format. The visual aggression is activation energy. It forces engagement. It makes ignoring the analysis psychologically harder than engaging with it.</p>

        <p>The danger is that this aesthetic discredits the analysis by making it seem hysterical or partisan. That's why every prediction includes 2025 signal validation with specific data sources, confidence scoring with methodology, and success probability calculations with ranges. The rhetoric is aggressive; the analysis underneath is rigorous. The combination is deliberate: emotional activation backed by analytical credibility.</p>

        <h2>What This Document Does Not Do</h2>
        <p>This is not a comprehensive futures analysis. It is a workforce planning tool focused on 36-month forecasting for creative industries. It does not cover every sector, every risk, or every opportunity. It covers scenarios where early detection enables prevention and where prevention is meaningfully cheaper than cure.</p>

        <p>This is not a policy recommendation document. Each prediction includes countermeasures, but these are options not prescriptions. Decision-makers must weigh tradeoffs, assess local contexts, and determine feasibility within their constraints. The model provides decision-support intelligence, not decisions.</p>

        <p>This is not deterministic prediction. Every scenario is conditional on current trajectories continuing. Exogenous shocks (policy changes, technological breakthroughs, economic crises, geopolitical disruptions) can invalidate trajectories overnight. The model assumes incrementalism and extrapolation, not revolution and transformation.</p>

        <p>This is not complete. The model continues to evolve (currently v5.17.0 with ongoing amendment development). Some sectors are more comprehensively modeled than others. Some data sources are proprietary and not universally accessible. Some confidence levels are lower than would be ideal for quantitative precision. These limitations are documented transparently throughout rather than hidden through false certainty.</p>

        <h2>Final Note on Uncertainty</h2>
        <p>This document presents 60 scenarios. Some will materialize. Some will not. Some will materialize differently than described. Some will be prevented by interventions. Some will be prevented by exogenous shocks. Some will worsen beyond forecasts. Some will improve beyond expectations.</p>

        <p>The certainty is not in specific outcomes. The certainty is in the system dynamics: when market demand drives investment drives hiring drives training, early signals in demand predict late outcomes in employment. When pressure builds in broken incentive structures, actors respond in predictable ways. When interventions happen within response windows, they succeed at calculable rates. When interventions happen outside response windows, they fail expensively.</p>

        <p>The goal is not perfect foresight. The goal is structured decision-making under uncertainty. Would you rather make workforce planning decisions with 36-month forecasts at 40-70% confidence or with no forecasts at 0% confidence? The model provides the former. The rest is judgment.</p>

        <p><strong>The futures described here are not inevitable. They're probable under current conditions. Change the conditions, change the probabilities. That's the entire point.</strong></p>

        <div style={{ marginTop: '20px', padding: '15px', background: '#2d3436', color: 'white', textAlign: 'center' }}>
          <p style={{ fontSize: '12pt', fontWeight: 700, margin: 0 }}>Contact: hello@obsolete.com</p>
          <p style={{ fontSize: '10pt', margin: '8px 0 0', opacity: 0.9 }}>Model Version 5.17.0 • Analysis Date: December 2024 • Forecast Horizon: 2029</p>
        </div>
      </div>
    </div>
  );
};

export default ExecutiveSummaryReport;
